import { frontendToolHandler } from 'app/services/aiChat';
import _ from 'lodash';

import { SIGNAL_R_AI_CHAT_NEGOTIATE_URL } from 'constants/api';
import { logger } from 'utils/logger';

import { refreshChatMessagesAction, setIsConnectedAction } from 'store/aiChat/actions';

import { aiChatSignalRConnection, aiChatSignalRController } from './index';
import { accessTokenFactory } from './signalR.utils';

const aiChatLifecycle = { initPromise: null, teardownPromise: null };

const registerConnectionStatusHandlers = dispatch => {
  aiChatSignalRConnection.onreconnecting(() => {
    dispatch(setIsConnectedAction(false));
  });

  // DEV-3061: the connection is built withAutomaticReconnect. On reconnect the
  // socket comes back with a NEW connection id, so the server-side JoinSession
  // group membership is lost and the AI Chat panel silently stops receiving
  // streamed response_token / tool / plan events until a manual remount.
  // Re-join the tracked session so live updates keep flowing (sibling of the
  // DEV-3055 fix in aiProcessRunsConnector).
  const rejoinSession = () => {
    aiChatSignalRController.resubscribe().catch(error => {
      logger.error('[AiChat SignalR] Failed to re-join session after reconnect', error);
    });
  };

  // DEV-4392: re-joining the group restores LIVE events, but anything emitted
  // during the connection gap (e.g. an ask_user_question pause) is lost —
  // SignalR does not replay. Re-fetch chat history so the projected
  // `hasPendingQuestions` flag re-fires the panel's rehydrate effect and the
  // question card recovers without a manual reload.
  const recoverMissedState = () => {
    dispatch(refreshChatMessagesAction()).catch(error => {
      logger.error('[AiChat SignalR] Failed to refresh messages after reconnect', error);
    });
  };

  aiChatSignalRConnection.onreconnected(() => {
    dispatch(setIsConnectedAction(true));
    rejoinSession();
    recoverMissedState();
  });

  // DEV-3108: the wake-from-sleep path does a cold start() that does NOT fire
  // onreconnected, so re-join the session on the wake hook too.
  aiChatSignalRConnection.onWakeReconnected(() => {
    dispatch(setIsConnectedAction(true));
    rejoinSession();
    recoverMissedState();
  });

  aiChatSignalRConnection.onclose(error => {
    dispatch(setIsConnectedAction(false));

    if (error) {
      logger.error('[AiChat SignalR] Connection closed with error', error);
    }
  });
};

const negotiate = async () => {
  const token = await accessTokenFactory();

  if (!token) {
    throw new Error('[AiChat SignalR] No valid access token — skipping negotiate');
  }

  const response = await fetch(SIGNAL_R_AI_CHAT_NEGOTIATE_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`[AiChat SignalR] Negotiate failed with status ${response.status}`);
  }

  return response.json();
};

// DEV-6376: SignalR calls this on every (re)connect, so a hub that outlives the short-lived Azure token
// negotiate hands back re-negotiates rather than reconnecting with an expired one. The negotiate itself
// goes through accessTokenFactory, which refreshes the Accounts session when needed.
const createAiChatAccessTokenFactory = initialAccessToken => {
  const negotiated = { accessToken: initialAccessToken, isFirstConnect: true };

  return async () => {
    if (negotiated.isFirstConnect) {
      negotiated.isFirstConnect = false;

      return negotiated.accessToken;
    }

    const { accessToken } = await negotiate();
    negotiated.accessToken = accessToken;

    return accessToken;
  };
};

const runAiChatConnectionInit = async (dispatch, getState) => {
  if (aiChatLifecycle.teardownPromise) {
    await aiChatLifecycle.teardownPromise.catch(_.noop);
  }

  try {
    if (!aiChatSignalRConnection.isConnected) {
      const { url, accessToken } = await negotiate();

      aiChatSignalRConnection.initAzure(url, createAiChatAccessTokenFactory(accessToken));
      registerConnectionStatusHandlers(dispatch);

      await aiChatSignalRConnection.start();
    }

    frontendToolHandler.connect({ dispatch, getState });
    aiChatSignalRController.connect({ dispatch, frontendToolHandler });
    dispatch(setIsConnectedAction(true));
  } catch (error) {
    logger.error('[AiChat SignalR] Failed to initialize connection', error);
    dispatch(setIsConnectedAction(false));
    aiChatLifecycle.initPromise = null;

    throw error;
  }
};

const runAiChatConnectionTeardown = async () => {
  if (aiChatLifecycle.initPromise) {
    await aiChatLifecycle.initPromise.catch(_.noop);
  }

  aiChatSignalRController.disconnect();
  frontendToolHandler.disconnect();

  if (aiChatSignalRConnection.isConnected) {
    try {
      await aiChatSignalRConnection.stop();
    } catch (error) {
      logger.error('[AiChat SignalR] Failed to stop connection', error);
    }
  }

  aiChatSignalRConnection.resetConnection();
  aiChatLifecycle.initPromise = null;
};

export const initAiChatConnection = (dispatch, getState) => {
  if (aiChatLifecycle.teardownPromise) {
    const pendingTeardown = aiChatLifecycle.teardownPromise;

    aiChatLifecycle.initPromise = pendingTeardown
      .catch(_.noop)
      .then(() => runAiChatConnectionInit(dispatch, getState));

    return aiChatLifecycle.initPromise;
  }

  if (aiChatLifecycle.initPromise) {
    return aiChatLifecycle.initPromise;
  }

  aiChatLifecycle.initPromise = runAiChatConnectionInit(dispatch, getState);

  return aiChatLifecycle.initPromise;
};

export const teardownAiChatConnection = () => {
  aiChatLifecycle.teardownPromise = runAiChatConnectionTeardown().finally(() => {
    aiChatLifecycle.teardownPromise = null;
  });

  return aiChatLifecycle.teardownPromise;
};
